import type { ReactNode } from "react";
import {
  useEffect,
  useState
} from "react";
import {
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLangStore } from "@app/dashboard/store/lang";
import * as amplitude from "@amplitude/analytics-browser";

import {
  DropdownTagSelector,
  FormatCurrencyForDisplay,
  ItemSelectorGroup,
  Modal,
  SkeletonBase,
  StatusIndicator,
} from "@unified-checkout/design-system";
import { MoneyBill } from "@unified-checkout/design-system/icons/index";

import {
  BILLS_STATUS,
  AMOUNT,
  FUND,
  BILLS,
  MODAL_STEP_STATUS,
  PHONE_NUMBER,
  YOUR_BALANCE_ID,
  MOBILE_WALLET,
  IS_FUND_SECTION,
} from "@app/dashboard/constants/Bills";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { DEBOUNCE_INPUT_TIME } from "@app/dashboard/constants/transfers";
import { SHARED_EVENTS_PARAMS } from "@app/dashboard/constants/tracking_events/shared";
import {
  BILLS_EVENTS,
  BILLS_EVENTS_PARAMS
} from "@app/dashboard/constants/tracking_events/bills";

import { confirmPayment } from "@app/dashboard/services/inner_layout/bills/confirm-payment";
import { getAvailableBalance } from "@app/dashboard/services/inner_layout/bills/get-available-balance";
import { confirmSmsSteps } from "@app/dashboard/services/inner_layout/bills/confirm-sms-steps";
import { getWalletData } from "@app/dashboard/services/inner_layout/bills/get-wallet-data";
import { confirmBalanceTopUp } from "@app/dashboard/services/inner_layout/bills/confirm-balance-top-up";
import { inquiryPayment } from "@app/dashboard/services/inner_layout/bills/inquiry-payment";
import { getBalanceDetails } from "@app/dashboard/services/inner_layout/bills/get-balance-details";

import type {
  IAddFund,
  IInitialFormState
} from "@app/dashboard/types/innerLayout/bills/add-fund";
import { MultiSectionCard } from "@app/dashboard/components/inner_layout/MultiSectionCard";
import {
  isStatusVisible,
  validateField,
} from "@app/dashboard/views/pages/inner_layout/Bills/utils";

import { DynamicField } from "../../AllServices/CategoryModal/DynamicField";
import styles from "./add-funds.module.scss";

const INITIAL_FORM_STATE: IInitialFormState = {
  phoneNumber: '',
  phoneNumberKey: 0,
  amount: '',
  selectedMerchantOption: undefined,
};

export const AddFundsModal = ({
  showAddFunds,
  setShowAddFunds,
  cardData,
  activeTab,
  setActiveTab,
}: IAddFund) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const lang = useLangStore();

  const [status, setStatus] = useState<string>(MODAL_STEP_STATUS.CONFIRM);
  const [formState, setFormState] = useState<typeof INITIAL_FORM_STATE>(INITIAL_FORM_STATE);
  const [debouncedValues, setDebouncedValues] = useState({ amount: '', phoneNumber: '' });
  const [initiatedAmount, setInitiatedAmount] = useState<number>(0);
  const [initiatedBalance, setInitiatedBalance] = useState<number>(0);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});


  const {
    data: walletData,
    isLoading: isWalletDataLoading,
    error: walletDataError,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_WALLET_DATA, lang],
    queryFn: getWalletData,
    enabled: activeTab === BILLS,
  });

  const selectWalletsItems = walletData?.data?.data ?? [];
  const selectedWallet = formState.selectedMerchantOption?.value ?? selectWalletsItems[0];
  const selectedServices = selectedWallet?.services ?? [];
  const categoryId = selectedWallet?.category_id ?? '';

  const feildRequired = selectedServices[0]?.fields_required;
  const servicesId = formState.selectedMerchantOption?.value?.services[0]?.service_id;

  const findField = (field_name: string) => feildRequired?.find((field: any) => field?.field_name === field_name);

  const {
    data: availableBalanceData,
    isLoading: isAvailableBalanceLoading,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_AVAILABLE_BALANCE, lang],
    queryFn: getAvailableBalance,
    enabled: activeTab === YOUR_BALANCE_ID,
  });

  const {
    display_amount: availableBalanceAmount,
    display_currency: availableBalanceCurrency,
    title: balanceTitle,
  } = availableBalanceData?.data?.data ?? {};

  const {
    mutate: handleConfirmAddFund,
    data: confirmAddFundData,
    error: confirmAddFundError,
    isPending: isConfirmAddFundPending,
    reset: confirmAddFundReset,
  } = useMutation({
    mutationFn: confirmPayment,
    onSuccess: () => {
      setStatus(MODAL_STEP_STATUS.SMS_STEPS);
      resetForm();
    },
    //onError: () => setStatus(MODAL_STEP_STATUS.FAILED),
  });

  const amountFieldError = errors[findField(AMOUNT)?.field_name];
  const hasValidAmount = !!formState.amount && !amountFieldError;
  const isAmountValid = !!debouncedValues.amount && !amountFieldError;

  const {
    data: billDetailsData,
    isFetching: isBillDetailsLoading,
    error: billDetailsError,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_BILLS_DETAILS, lang, debouncedValues.amount],
    queryFn: () => inquiryPayment({
      category_id: categoryId,
      ...(servicesId && { service_id: servicesId }),
      required_data: {
        phone_number: debouncedValues.phoneNumber,
        amount: debouncedValues.amount,
        fund: findField(FUND)?.default_value,
        is_fund_section: findField(IS_FUND_SECTION)?.default_value,
      },
    }),
    enabled: activeTab === BILLS && isAmountValid,
  });

  const {
    data: balanceDetailsData,
    isFetching: isBalanceDetailsLoading,
    error: balanceDetailsError,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_ADD_FUND_BALANCE_DETAILS, debouncedValues.amount, lang],
    queryFn: () => getBalanceDetails(debouncedValues.amount),
    enabled: activeTab === YOUR_BALANCE_ID && isAmountValid,
  });

  const billsDetails = billDetailsData?.data?.data?.bill_details ?? {};
  const balanceDetails = balanceDetailsData?.data?.data?.bill_details ?? {};
  const activePaymentDetailsData = activeTab === BILLS ? billDetailsData : balanceDetailsData;

  const activePaymentDetails = isAmountValid
    ? activePaymentDetailsData?.data?.data?.payment_details
    : null;

  const paymentDetails: {
    billFees: any[];
    cashbackData: any;
  } = {
    billFees: activePaymentDetails?.details ?? [],
    cashbackData: activePaymentDetails?.cashback_data ?? {},
  };

  const isPaymentDetailsLoading = isBillDetailsLoading || isBalanceDetailsLoading;

  const {
    mutate: handleConfirmBalanceTopUp,
    data: confirmBalanceTopUpData,
    error: confirmBalanceTopUpError,
    isPending: isConfirmBalanceTopUpPending,
  } = useMutation({
    mutationFn: (payload: { amount: number; fees: number; trackingData: any }) => confirmBalanceTopUp(payload),
    onSuccess: (res, variables) => {
      const { trackingData } = variables;
      amplitude.track(BILLS_EVENTS.BILLS_TOP_UP_CONFIRMED_STATUS, {
        [BILLS_EVENTS_PARAMS.AMOUNT]: trackingData.amount,
        [BILLS_EVENTS_PARAMS.REMAINING_BILLS_BALANCE]: trackingData.remainingBalance,
        [BILLS_EVENTS_PARAMS.STATUS]: res?.data?.status || 'success',
      });
      setStatus(MODAL_STEP_STATUS.PENDING);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MAIN_DATA] });
      resetForm();
    },
    onError: (error: any, variables) => {
      const { trackingData } = variables;
      amplitude.track(BILLS_EVENTS.BILLS_TOP_UP_CONFIRMED_STATUS, {
        [BILLS_EVENTS_PARAMS.AMOUNT]: trackingData.amount,
        [BILLS_EVENTS_PARAMS.REMAINING_BILLS_BALANCE]: trackingData.remainingBalance,
        [BILLS_EVENTS_PARAMS.STATUS]: error?.response?.data?.status || 'failed',
        [SHARED_EVENTS_PARAMS.ERR_DESCRIPTION]: error?.response?.data?.message,
      });
      setStatus(MODAL_STEP_STATUS.FAILED)
    },
  });
  const instructions = confirmAddFundData?.data?.instructions;
  const checkSms = confirmAddFundData?.data?.status_check;

  const {
    mutate: checkSmsStatus,
    isPending: isCheckSmsStatusPending,
    data: checkSmsStatusData,
    error: checkSmsStatusError,
  } = useMutation({
    mutationFn: (checkSmsStatusData: string) => confirmSmsSteps(checkSmsStatusData),
    onSuccess: (res) => {
      amplitude.track(BILLS_EVENTS.BILLS_WALLET_NEXT_STEPS_POP_UP_CONTINUE_BUTTON_SELECTED, {
        [BILLS_EVENTS_PARAMS.AMOUNT]: initiatedAmount,
        [BILLS_EVENTS_PARAMS.REMAINING_BILLS_BALANCE]: initiatedBalance + initiatedAmount,
        [BILLS_EVENTS_PARAMS.STATUS]: res?.data?.status || 'success',
      });
      setStatus(MODAL_STEP_STATUS.SUCCESS);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MAIN_DATA] });
      resetForm();
    },
    onError: (error: any) => {
      amplitude.track(BILLS_EVENTS.BILLS_WALLET_NEXT_STEPS_POP_UP_CONTINUE_BUTTON_SELECTED, {
        [BILLS_EVENTS_PARAMS.AMOUNT]: initiatedAmount,
        [BILLS_EVENTS_PARAMS.REMAINING_BILLS_BALANCE]: initiatedBalance + initiatedAmount,
        [BILLS_EVENTS_PARAMS.STATUS]: error?.response?.data?.status || 'failed',
        [SHARED_EVENTS_PARAMS.ERR_DESCRIPTION]: error?.response?.data?.message,
      });
      setStatus(MODAL_STEP_STATUS.FAILED)
    },
  });

  useEffect(() => {
    if (!showAddFunds || isWalletDataLoading) {
      return;
    }

    amplitude.track(BILLS_EVENTS.BILLS_ADD_FUNDS_PAGE_VIEWED, {
      [BILLS_EVENTS_PARAMS.STATUS]: walletData?.data?.status,
      [SHARED_EVENTS_PARAMS.ERR_DESCRIPTION]: (walletDataError as any)?.response?.data?.message,
    });

    amplitude.track(BILLS_EVENTS.BILLS_MOBILE_WALLET_TOP_UP_SELECTED, {
      [BILLS_EVENTS_PARAMS.METHOD]: MOBILE_WALLET,
      [BILLS_EVENTS_PARAMS.TYPE]: Number(availableBalanceAmount) > Number(formState.amount) ? "sufficient" : "insufficient"
    });
  }, [showAddFunds, isWalletDataLoading, walletData, walletDataError]);

  useEffect(() => {
    if (status === MODAL_STEP_STATUS.SMS_STEPS) {
      amplitude.track(BILLS_EVENTS.BILLS_WALLET_NEXT_STEPS_POP_UP_VIEWED, {
        [BILLS_EVENTS_PARAMS.STATUS]: confirmAddFundData?.data?.status,
      });
    }
  }, [status]);

  useEffect(() => {
    if (!hasValidAmount) {
      setDebouncedValues({ amount: '', phoneNumber: '' });
      return;
    }

    const timeout = setTimeout(() => {
      setDebouncedValues({
        amount: formState.amount,
        phoneNumber: formState.phoneNumber,
      });
    }, DEBOUNCE_INPUT_TIME);

    return () => clearTimeout(timeout);
  }, [activeTab, formState.amount, amountFieldError]);

  const paymentDetailsError = (billDetailsError as any)?.response?.data?.message || (balanceDetailsError as any)?.response?.data?.message;

  const isFormValid = activeTab === BILLS
    ? (!!formState.phoneNumber && !errors[findField(PHONE_NUMBER)?.field_name] && !!formState.selectedMerchantOption && !!formState.amount && !errors[findField(AMOUNT)?.field_name])
    : (!!formState.amount && !errors[findField(AMOUNT)?.field_name]) && Number(availableBalanceAmount) > Number(formState.amount);

  const isConfirmDisabled = () => {
    if (status === MODAL_STEP_STATUS.CONFIRM) {
      return !isFormValid || isPaymentDetailsLoading || isConfirmAddFundPending || isConfirmBalanceTopUpPending || paymentDetailsError;
    } else if (status === MODAL_STEP_STATUS.SMS_STEPS) {
      return isCheckSmsStatusPending;
    }
    return false;
  }
  const confirmErrorMessage = (confirmAddFundError as any)?.response?.data?.description;

  const resetForm = () => {
    setFormState((prev) => ({
      ...INITIAL_FORM_STATE,
      phoneNumberKey: prev.phoneNumberKey + 1,
    }));
    setDebouncedValues({ amount: '', phoneNumber: '' });
    setErrors({});
  };

  const handlePhoneChange = (phone: string) => {
    setFormState((prev) => ({ ...prev, phoneNumber: phone }));
    setErrors((prev) => ({
      ...prev,
      [findField(PHONE_NUMBER)?.field_name]: validateField(findField(PHONE_NUMBER), phone),
    }));
  };

  const handleAmountChange = (value: string) => {
    setFormState((prev) => ({ ...prev, amount: value }));
    setErrors((prev) => ({
      ...prev,
      [findField(AMOUNT)?.field_name]: validateField(findField(AMOUNT), value),
    }));
  };

  const handleTabChange = (tabTitle: string) => {
    setActiveTab(tabTitle);
    resetForm();

    const isMobileWallet = tabTitle === BILLS;
    const event = isMobileWallet
      ? BILLS_EVENTS.BILLS_MOBILE_WALLET_TOP_UP_SELECTED
      : BILLS_EVENTS.BILLS_YOUR_BALANCE_TOP_UP_SELECTED;

    amplitude.track(event, {
      [BILLS_EVENTS_PARAMS.METHOD]: tabTitle,
      [BILLS_EVENTS_PARAMS.TYPE]: Number(availableBalanceAmount) > Number(formState.amount) ? "sufficient" : "insufficient"
    });
    confirmAddFundReset();
  };

  const handleAddFundsClose = () => {
    setShowAddFunds(false);
    resetForm();
    setStatus(MODAL_STEP_STATUS.CONFIRM);
    confirmAddFundReset();
  };

  const getModalTitle = () => {
    if (status === MODAL_STEP_STATUS.CONFIRM) {
      return t('innerLayout.billsPage.addFundsModal.title');
    } else if (status === MODAL_STEP_STATUS.SMS_STEPS) {
      return t('innerLayout.billsPage.addFundsModal.smsStepsTitle');
    }

    return '';
  };
  const handleConfirmAction = () => {
    if (status === MODAL_STEP_STATUS.CONFIRM) {
      const amount = Number(formState.amount);
      const method = activeTab;

      amplitude.track(BILLS_EVENTS.BILLS_TOP_UP_CONFIRMED, {
        [BILLS_EVENTS_PARAMS.METHOD]: method,
        [BILLS_EVENTS_PARAMS.AMOUNT]: amount,
      });

      if (activeTab === BILLS) {
        setInitiatedAmount(amount);
        setInitiatedBalance(Number(cardData?.amount));
        handleConfirmAddFund(billsDetails);
      } else {
        handleConfirmBalanceTopUp({
          ...balanceDetails,
          trackingData: {
            amount,
            remainingBalance: Number(availableBalanceAmount) - amount,
          }
        });
      }
    }
    if (status === MODAL_STEP_STATUS.SMS_STEPS) {
      checkSmsStatus(checkSms)
    }
  }
  const addFundsStatusContent = BILLS_STATUS().find((item) => item.id === status);

  const getBackendMessage = () => {
    let title;
    let desc;

    if (activeTab === BILLS) {
      if (checkSmsStatusData) {
        title = checkSmsStatusData?.data?.title
        desc = checkSmsStatusData?.data?.description
      } else {
        title = (checkSmsStatusError as any)?.response?.data?.message
        desc = (checkSmsStatusError as any)?.response?.data?.description
      }
    } else {
      if (confirmBalanceTopUpData) {
        title = confirmBalanceTopUpData?.data?.title;
        desc = confirmBalanceTopUpData?.data?.message;
      } else {
        title = (confirmBalanceTopUpError as any)?.response?.data?.status;
        desc = (confirmBalanceTopUpError as any)?.response?.data?.message;;
      }
    }
    return { title, desc }
  }
  const { title, desc } = getBackendMessage();

  const modalBtnsProps = !isStatusVisible(status) ? [
    {
      text: status === MODAL_STEP_STATUS.CONFIRM ?
        t('innerLayout.billsPage.transferModal.confirmButton')
        :
        t('innerLayout.billsPage.transferModal.smsConfirmButton'),
      onClickHandler: handleConfirmAction,
      disabled: isConfirmDisabled(),
      loading: isConfirmAddFundPending || isConfirmBalanceTopUpPending || isCheckSmsStatusPending,
    },
    {
      color: "secondary" as const,
      variant: "outlined" as const,
      text: t('innerLayout.billsPage.transferModal.cancelButton'),
      onClickHandler: handleAddFundsClose,
    },
  ] : undefined;


  const loader = ({
    section,
    height,
    width,
    key,
    noOfLoader,
    isLoading,
    loaderType = 'single_item',
  }: {
    section: ReactNode;
    height?: string;
    width?: string;
    key: string;
    isLoading: boolean;
    noOfLoader?: number;
    loaderType?: 'single_item' | 'key_value_pair';
  }): ReactNode => {
    return isLoading ? (
      <>
        {loaderType === 'key_value_pair' ?
          <div>
            {Array.from({ length: noOfLoader ?? 1 }, (_, index) =>
              <div className={styles['modal__body__fees-info__item']} key={index}>
                <SkeletonBase height={height} width={width} />
                <SkeletonBase height={height} width={width} />
              </div>
            )}
          </div>
          :
          Array.from({ length: noOfLoader ?? 1 }, (_, index) =>
            <SkeletonBase
              height={height}
              width={width}
              key={`${key}-${index}`}
            />
          )}
      </>
    ) : section;
  };

  const renderItemSelector = () => {
    const itemSelectorData = cardData?.add_funds ?? [];

    return (
      <div className={styles['modal__body__method-selector']}>
        <ItemSelectorGroup
          label={t('innerLayout.billsPage.addFundsModal.method')}
          type="radio"
          orientation="horizontal"
          hasFirstItemSelected={false}
          items={itemSelectorData.map((item: any) => ({
            ...item,
            text: item.title,
            hintText: item.sub_title,
            selected: activeTab === item.redirection_screen,
            size: 'sm',
            disabled: isPaymentDetailsLoading || isAvailableBalanceLoading,
            onClickHandler: () => handleTabChange(item.redirection_screen),
          }))}
        />
      </div>
    )
  }
  const renderWalletStep = () => {
    const walletOptions = selectWalletsItems.map((wallet: any) => ({
      id: String(wallet?.service_provider ?? ""),
      label: wallet?.service_provider ?? "",
      value: wallet,
      searchKey: wallet?.service_provider ?? "",
    }));

    return (
      activeTab === BILLS && (
        <>
          {loader({
            isLoading: isWalletDataLoading,
            noOfLoader: 1,
            height: "4rem",
            key: "wallet-dropdown",
            section: (
              <DropdownTagSelector
                isMultiple={false}
                isResetSearchQueryOnToggle={false}
                dropdownProps={{ label: t('innerLayout.billsPage.addFundsModal.wallet') }}
                optionsList={walletOptions}
                selectedOptions={formState.selectedMerchantOption ? [formState.selectedMerchantOption] : []}
                onSelectionChange={(items) => {
                  setFormState((prev) => ({
                    ...prev,
                    selectedMerchantOption: items[0],
                  }));
                }}
                tagsInputProps={{
                  name: 'bills-transfer-merchant',
                  placeholder: t('innerLayout.billsPage.addFundsModal.selectWallet'),
                  getTagShapeProps: (option) => ({
                    id: option.id,
                    text: option.label,
                    type: 'user' as const,
                    img: option.value?.service_icon,
                  }),
                }}
                renderDropdownMenuItem={(option) => (
                  <div className={styles['modal__body__wallet-option']}>
                    <img src={option.value?.service_icon} alt={option.label} />
                    <div>{option.label}</div>
                  </div>
                )}
              />
            ),
          })}
        </>
      )
    )
  }

  const renderDynamicFeild = () => {
    return loader({
      isLoading: isWalletDataLoading,
      noOfLoader: activeTab === BILLS ? 2 : 1,
      height: "4rem",
      key: "dynamic-fields",
      section: (
        <>
          {feildRequired?.map((field: any) => {
            if (field.field_type === PHONE_NUMBER && activeTab !== BILLS) return;
            if (field.is_hidden) return;

            return (
              <div key={field.field_name} className={field.field_type === AMOUNT ? styles['modal__body__amount-input'] : undefined}>
                <DynamicField
                  key={field.field_name === PHONE_NUMBER ? `${field.field_name}-${formState.phoneNumberKey}` : field.field_name}
                  field={field}
                  value={field.field_name === AMOUNT ? formState.amount : formState.phoneNumber}
                  error={errors[field.field_name] || (field.field_name === AMOUNT ? paymentDetailsError : undefined)}
                  onChange={(fieldName, value) => {
                    const stringValue = String(value ?? '');
                    if (fieldName === AMOUNT) handleAmountChange(stringValue);
                    if (fieldName === PHONE_NUMBER) handlePhoneChange(stringValue);
                  }}
                  disabled={field.field_type === AMOUNT ? (isAvailableBalanceLoading || (activeTab === BILLS && !formState.phoneNumber) || !!errors[findField(PHONE_NUMBER)?.field_name]) : false}
                />
                {(field.field_type === AMOUNT && activeTab === YOUR_BALANCE_ID) &&
                  loader({
                    isLoading: isAvailableBalanceLoading,
                    noOfLoader: 1,
                    width: "9rem",
                    height: "1rem",
                    key: "balance",
                    section:
                      !!availableBalanceAmount && (
                        <div className={styles['modal__body__amount-input__balance']}>
                          <div className={styles['modal__body__amount-input__balance__label']}>
                            {balanceTitle}:
                          </div>
                          <div className={styles['modal__body__amount-input__balance__value']}>
                            {availableBalanceCurrency} {availableBalanceAmount}
                          </div>
                        </div>
                      ),
                  })}
              </div>
            );
          })}
        </>
      ),
    })
  }
  const renderFees = () => {
    return loader({
      isLoading: isPaymentDetailsLoading,
      noOfLoader: activeTab !== BILLS ? 3 : 4,
      loaderType: 'key_value_pair',
      height: "1rem",
      width: "5rem",
      key: "fees",
      section:
        paymentDetails?.billFees?.length > 0 && (
          <div className={styles['modal__body__fees-info']}>
            {paymentDetails.billFees.map((fee, index) => (
              <div
                className={styles['modal__body__fees-info__item']}
                key={index}
              >
                <div className={styles['modal__body__fees-info__item__key']}>
                  {fee.title}
                </div>
                <div className={styles['modal__body__fees-info__item__value']}>
                  {fee.value || "-"}
                </div>
              </div>
            ))}
          </div>
        ),
    })
  }
  const renderCashback = () => {
    return loader({
      isLoading: isPaymentDetailsLoading,
      noOfLoader: 1,
      height: "3rem",
      width: "100%",
      key: "cashback",
      section: (
        !!paymentDetails.cashbackData.title && (
          <div className={styles['modal__body__note']}>
            <div
              className={styles['modal__body__note__title']}
              style={{ color: paymentDetails.cashbackData.title_color }}
            >
              <img src={paymentDetails.cashbackData.icon} alt="icon" />
              {paymentDetails.cashbackData.title}
            </div>
            <div className={styles['modal__body__note__description']}>
              {paymentDetails.cashbackData.sub_title}
            </div>
          </div>
        )
      ),
    })
  }


  const renderConfirmStep = () => (
    <>
      {renderItemSelector()}
      {renderWalletStep()}
      {renderDynamicFeild()}
      {renderFees()}
      {renderCashback()}
    </>
  )

  const renderSmsSteps = () => (
    <>
      {instructions?.map((step: {
        icon: string,
        title: string,
        description: string,
      }, index: number) => (
        <MultiSectionCard
          key={index}
          title={step.title}
          subtitle={step.description}
          icon={step.icon}
        />
      ))}
    </>
  );

  return (
    <div className={styles['modal']}>
      <Modal
        state="primary"
        headerIcon={!isStatusVisible(status) ? MoneyBill : undefined}
        title={getModalTitle()}
        hasCloseIcon
        open={showAddFunds}
        onCloseHandler={handleAddFundsClose}
        position="center"
        closeOnClickOutside={false}
        errorMsg={confirmErrorMessage}
        btnsProps={modalBtnsProps}
      >
        <div className={styles['modal__body']}>
          {isStatusVisible(status) ? (
            <StatusIndicator
              status={addFundsStatusContent?.status}
              icon={addFundsStatusContent?.icon}
              text={title}
              hintText={desc}
            />
          ) :
            status === MODAL_STEP_STATUS.CONFIRM ?
              renderConfirmStep()
              :
              renderSmsSteps()
          }
        </div>
      </Modal>
    </div>
  );
};
